import gsap from 'gsap';

interface KenBurnsOptions {
  burnsDuration?: number;
  transitionTime?: number;
  timeout?: number;
}

interface KenBurnsPattern {
  fromScale: number;
  toScale: number;
  fromXPercent: number;
  toXPercent: number;
  fromYPercent: number;
  toYPercent: number;
}

const PRESETS: Record<string, KenBurnsPattern> = {
  zoomIn: {
    fromScale: 1,
    toScale: 1.5,
    fromXPercent: 0,
    toXPercent: 0,
    fromYPercent: 0,
    toYPercent: 0,
  },
  panUp: {
    fromScale: 1.3,
    toScale: 1.3,
    fromXPercent: 0,
    toXPercent: 0,
    fromYPercent: 10,
    toYPercent: -10,
  },
  panRight: {
    fromScale: 1.3,
    toScale: 1.3,
    fromXPercent: -10,
    toXPercent: 10,
    fromYPercent: 0,
    toYPercent: 0,
  },
  panDown: {
    fromScale: 1.3,
    toScale: 1.3,
    fromXPercent: 0,
    toXPercent: 0,
    fromYPercent: -10,
    toYPercent: 10,
  },
  panLeft: {
    fromScale: 1.3,
    toScale: 1.3,
    fromXPercent: 10,
    toXPercent: -10,
    fromYPercent: 0,
    toYPercent: 0,
  },
  zoomOut: {
    fromScale: 1.5,
    toScale: 1,
    fromXPercent: 0,
    toXPercent: 0,
    fromYPercent: 0,
    toYPercent: 0,
  },
};

export class KenBurns {
  private container: HTMLElement;
  private images: HTMLElement[];
  private current: number = 0;
  private burnsDuration: number;
  private transitionTime: number;
  private timeout: number;
  private imageTweens: Map<number, gsap.core.Tween> = new Map();
  private delayedCallRef: gsap.core.Tween | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimeout: number | null = null;
  private lastWidth: number = 0;
  private patternCounter: number = 0;

  private readonly patterns: KenBurnsPattern[] = [
    PRESETS.zoomOut,
    PRESETS.panRight,
    PRESETS.panDown,
    PRESETS.zoomIn,
    PRESETS.panDown,
    PRESETS.panUp,
  ];

  constructor(selector: string, options: KenBurnsOptions = {}) {
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) {
      throw new Error(`Element not found: ${selector}`);
    }

    this.container = container;
    this.images = Array.from(
      container.querySelectorAll<HTMLElement>('.ken-burns__img')
    );

    // オプション設定
    this.burnsDuration = options.burnsDuration ?? 15;
    this.transitionTime = options.transitionTime ?? 2;
    this.timeout = options.timeout ?? 5;

    this.init();
  }

  private init(): void {
    this.lastWidth = this.container.offsetWidth;

    // 最初の画像のtransformを先に設定（CSS側で全画像が非表示のため見えない）
    this.startImageBurns(0, this.patternCounter);
    this.patternCounter++;

    // Step 1: visibility を inherit に変更してコンポジターレイヤーを事前作成
    //         （opacity: 0 のままなので視覚的には見えない）
    gsap.set(this.images[0], { visibility: 'inherit' });

    // Step 2: 2フレーム待ってコンポジターが安定してからフェードイン
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        gsap.to(this.images[0], { opacity: 1, duration: 0.3 });
      });
    });

    // スライド自動再生開始
    this.autoPlay();

    // リサイズ対応
    this.setupResizeObserver();
  }

  private startImageBurns(imgIndex: number, patternIndex: number): void {
    this.killImageTween(imgIndex);
    const img = this.images[imgIndex];
    const pattern = this.patterns[patternIndex % this.patterns.length];

    gsap.set(img, {
      scale: pattern.fromScale,
      xPercent: pattern.fromXPercent,
      yPercent: pattern.fromYPercent,
      transformOrigin: 'center center',
    });

    const tween = gsap.to(img, {
      scale: pattern.toScale,
      xPercent: pattern.toXPercent,
      yPercent: pattern.toYPercent,
      duration: this.burnsDuration,
      ease: 'none',
    });

    this.imageTweens.set(imgIndex, tween);
  }

  private killImageTween(imgIndex: number): void {
    const existing = this.imageTweens.get(imgIndex);
    if (existing) {
      existing.kill();
      this.imageTweens.delete(imgIndex);
    }
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const newWidth = entry.contentRect.width;
      // 横幅が変わっていない場合はスキップ（iOSアドレスバーの高さ変動を無視）
      if (newWidth === this.lastWidth) return;
      this.lastWidth = newWidth;

      if (this.resizeTimeout) {
        window.clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = window.setTimeout(() => {
        this.handleResize();
      }, 150);
    });

    this.resizeObserver.observe(this.container);
  }

  private handleResize(): void {
    // 全 Tween を停止
    this.imageTweens.forEach((tween) => tween.kill());
    this.imageTweens.clear();
    this.delayedCallRef?.kill();
    this.delayedCallRef = null;

    // transform をリセット
    this.images.forEach((img) => {
      gsap.set(img, { clearProps: 'scale,xPercent,yPercent,transformOrigin' });
    });

    // 現在の画像で Ken Burns を再開（同じパターンを再利用）
    this.startImageBurns(this.current, this.patternCounter - 1);
    this.autoPlay();
  }

  public destroy(): void {
    if (this.resizeTimeout) {
      window.clearTimeout(this.resizeTimeout);
    }
    this.resizeObserver?.disconnect();
    this.imageTweens.forEach((tween) => tween.kill());
    this.imageTweens.clear();
    this.delayedCallRef?.kill();
    this.images.forEach((img) => gsap.killTweensOf(img));
  }

  private goTo(index: number): void {
    if (index === this.current) return;
    const prevIndex = this.current;

    // 新画像の Ken Burns を開始（フェードイン開始と同時）
    this.startImageBurns(index, this.patternCounter);
    this.patternCounter++;

    gsap
      .timeline({
        onComplete: () => {
          this.killImageTween(prevIndex);
          gsap.set(this.images[prevIndex], {
            clearProps: 'scale,xPercent,yPercent,transformOrigin',
          });
          this.autoPlay();
        },
      })
      .to(this.images[prevIndex], {
        autoAlpha: 0,
        duration: this.transitionTime,
        ease: 'none',
      })
      .to(
        this.images[index],
        {
          autoAlpha: 1,
          duration: this.transitionTime,
          ease: 'none',
        },
        0
      );

    this.current = index;
  }

  private autoPlay(): void {
    const nextIndex =
      this.current + 1 > this.images.length - 1 ? 0 : this.current + 1;
    this.delayedCallRef = gsap.delayedCall(this.timeout, () =>
      this.goTo(nextIndex)
    );
  }
}
